import { Message, readMessage } from "@/api/openapi";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
// this is to sanitize the html to make sure no <script> injections
// or other malicious stuff happens
import DOMPurify from "dompurify";
import { formatDateFromLang } from "@/config/utils";

export default function ReadMessages() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang } = useContext(PspContext);
  const { messageId } = useParams();
  const navigate = useNavigate();
  const isTablet = useIsTablet();

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [message, setMessage] = useState<Message>();
  const [isLoadingNotification, setIsLoadingNotification] = useState(true);

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (messageId) {
      readMessage({ path: { message_id: messageId } }).then(
        ({ data }) => {
          if (!data) throw new Error("Could not read message.");
          setMessage(data);
          setIsLoadingNotification(false);
        },
        () => {
          toast(translate("couldntLoadMessage", lang));
        },
      );
    }
  }, [lang, messageId]);

  // -----------------------------------------------------------------------------
  // MEMOIZED VALUES
  // -----------------------------------------------------------------------------
  const isLoading = useMemo(
    () => isLoadingNotification,
    [isLoadingNotification],
  );

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (!message)
    return (
      <p className="text-font-secondary">
        {translate("couldntFindMessage", lang)}
      </p>
    );

  return (
    <>
      {!isTablet && (
        <TopBarMobile
          headerString={`${message.subject} - ${formatDateFromLang(
            new Date(message.createdAt),
            lang,
          )}`}
          onBack={() => navigate(-1)}
        />
      )}
      <div className="p-6 tablet:p-10">
        {isTablet && (
          <div className="flex flex-row justify-between items-center align-middle mb-8">
            <h1 className="text-xl">{message.subject}</h1>
            <h2 className="text-font-secondary">
              {formatDateFromLang(new Date(message.createdAt), lang)}
            </h2>
          </div>
        )}
        <div
          className="break-all"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }}
        />
      </div>
    </>
  );
}
